<template>
	<b-modal id="receive-dispatch" ref="receive-dispatch" :title="title" size="lg" ok-title="Receive" ok-variant="success"
		@show="onReset" @ok="handleOk" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
		:no-close-on-backdrop="true" hide-header-close>
		<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

		<b-container fluid>
			<div>
				<b-row class="mb-3">
					<b-col sm="12">
						<i class="icon-info"></i>&nbsp; To <b-badge pill variant="success">RECEIVE</b-badge> dispatch
						<span class="numFont"><strong>{{ selDispatch.dispatchId }}</strong></span> for
						<b>{{ recipient }}</b>, please complete the details below:
					</b-col>
				</b-row>

				<!-- Primary Information Card -->
				<b-card>
					<b-row class="mt-0 mb-2">
						<b-col sm="12">
							<b>PRIMARY INFORMATION</b>
						</b-col>
					</b-row>
					<b-row class="mt-2">
						<b-col lg="6" md="6" sm="12" class="my-1">
							<b-form-group label="SOURCE" label-class="font-weight-bold pt-0">
								{{ selDispatch.source ? selDispatch.source.company : '' }}
							</b-form-group>
							<b-form-group label="DESTINATION" label-class="font-weight-bold pt-0">
								{{ selDispatch.destination ? selDispatch.destination.company : '' }}
							</b-form-group>
						</b-col>
						<b-col lg="6" md="6" sm="12" class="my-1">
							<b-form-group label="DATE RECEIVED" label-class="font-weight-bold pt-0">
								<date-range-picker opens="center" :single-date-picker="true" :timePicker="true"
									:timePicker24Hour="false" v-model="dateReceived" applyLabel="Apply" cancelLabel="Cancel"
									:style="{ width: '100%' }">
									<div slot="input">{{ dateReceivedDisplay }}</div>
								</date-range-picker>
							</b-form-group>
							<b-form-group label="REMARKS" label-class="font-weight-bold pt-0">
								{{ selDispatch.notes || '-' }}
							</b-form-group>
						</b-col>
					</b-row>
				</b-card>

				<!-- Tabs: Assets & Proof of Receipt -->
				<b-tabs class="mt-3">
					<!-- Assets Tab -->
					<b-tab title="Assets" active>
						<b-row class="mt-2">
							<b-col lg="12">
								<p><i>Review and update asset conditions before receiving</i></p>

								<b-table
									striped
									hover
									:items="assetsToReceive"
									:fields="assetFields"
									responsive
									show-empty>

									<template v-slot:cell(assetCode)="row">
										<span class="numFont">{{ row.item.assetCode }}</span>
									</template>

									<template v-slot:cell(condition)="row">
										<b-form-select
											v-model="assetConditionsMap[row.item.assetCode]"
											:options="getConditionOptions()"
											size="sm"
											:class="getConditionClass(assetConditionsMap[row.item.assetCode])">
										</b-form-select>
									</template>
								</b-table>
							</b-col>
						</b-row>
					</b-tab>

					<!-- Proof of Receipt Tab -->
					<b-tab title="Proof of Receipt">
						<b-row class="my-3">
							<b-col lg="6" md="6" sm="12">
								<b-form-group label="DOCUMENTS" label-for="Proof of Receipt" label-class="font-weight-bold pt-0">
									<small class="text-muted">(Optional - up to 3 images)</small>
								</b-form-group>

								<!-- Document 1 -->
								<div v-show="noOfDocuments >= 1">
									<b-row class="mt-4" no-gutters>
										<b-col sm="11">
											<b-form-group label="Document 1" label-for="Document 1"
												description="*PNG/JPG, 1000px by 1000px, up to 1 MB">
												<b-form-file id="Document 1" v-model="files[0]" placeholder="Choose image"
													ref="document-1" accept="image/png, image/jpeg, image/jpg"
													@change="onSelectDocument($event, 0)">
													<template slot="file-name" slot-scope="{ names }">
														<b-badge variant="dark">{{ names[0] }}</b-badge>
													</template>
												</b-form-file>
											</b-form-group>
										</b-col>
									</b-row>

									<!-- Preview -->
									<div class="document-file" v-if="isNonNullDocument(0)">
										<i @click="onShowDocument(selDocuments[0])">
											{{ selDocuments[0].name }}
										</i>
										<em @click="onResetDocument(0)" class="fa fa-times-circle"></em>
									</div>
								</div>

								<!-- Document 2 -->
								<div v-show="noOfDocuments >= 2">
									<b-row class="mt-4" no-gutters>
										<b-col sm="11">
											<b-form-group label="Document 2" label-for="Document 2"
												description="*PNG/JPG, 1000px by 1000px, up to 1 MB">
												<b-form-file id="Document 2" v-model="files[1]" placeholder="Choose image"
													ref="document-2" accept="image/png, image/jpeg, image/jpg"
													@change="onSelectDocument($event, 1)">
													<template slot="file-name" slot-scope="{ names }">
														<b-badge variant="dark">{{ names[0] }}</b-badge>
													</template>
												</b-form-file>
											</b-form-group>
										</b-col>
									</b-row>

									<!-- Preview -->
									<div class="document-file" v-if="isNonNullDocument(1)">
										<i @click="onShowDocument(selDocuments[1])">
											{{ selDocuments[1].name }}
										</i>
										<em @click="onResetDocument(1)" class="fa fa-times-circle"></em>
									</div>
								</div>

								<!-- Document 3 -->
								<div v-show="noOfDocuments >= 3">
									<b-row class="mt-4" no-gutters>
										<b-col sm="11">
											<b-form-group label="Document 3" label-for="Document 3"
												description="*PNG/JPG, 1000px by 1000px, up to 1 MB">
												<b-form-file id="Document 3" v-model="files[2]" placeholder="Choose image"
													ref="document-3" accept="image/png, image/jpeg, image/jpg"
													@change="onSelectDocument($event, 2)">
													<template slot="file-name" slot-scope="{ names }">
														<b-badge variant="dark">{{ names[0] }}</b-badge>
													</template>
												</b-form-file>
											</b-form-group>
										</b-col>
									</b-row>

									<!-- Preview -->
									<div class="document-file" v-if="isNonNullDocument(2)">
										<i @click="onShowDocument(selDocuments[2])">
											{{ selDocuments[2].name }}
										</i>
										<em @click="onResetDocument(2)" class="fa fa-times-circle"></em>
									</div>
								</div>

								<b-button class="add-document" variant="primary" @click="onAddDocument"
									v-show="noOfDocuments < 3">
									Add Document
								</b-button>
							</b-col>

							<b-col lg="6" md="6" sm="12">
								<b-form-group label="REMARKS" label-for="Remarks" label-class="font-weight-bold pt-0">
									<small class="text-muted">(Optional - max 200 characters)</small>
									<b-form-textarea name="Remarks" type="text" v-model="proofOfReceipt.remarks"
										maxlength="200" :rows="3" placeholder="Enter receipt remarks" />
									<span v-show="errors.has('Remarks')" class="help-block">
										{{ errors.first("Remarks") }}
									</span>
								</b-form-group>
							</b-col>
						</b-row>
					</b-tab>
				</b-tabs>
			</div>
		</b-container>
	</b-modal>
</template>

<script>
// Util
import { DateUtil } from '@/utils/dateutil';
import { UserUtil } from '@/utils/userutil';

// API
import dispatchApi from '@/api/dispatchApi';

// DAO
import assetDAO from '@/database/assets';

// Others
import config from '@/config/env-constants';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { storage } from '@/config/firebase';
import moment from 'moment';
import _ from 'lodash';

export default {
	name: 'receive-dispatch',
	components: {
		DateRangePicker,
		Loading,
	},
	props: {
		allUsersObj: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			selDispatch: {},
			recipient: '',

			loggedUserCompany: this.$store.getters.loggedUserCompany,
			loggedUser: this.$store.getters.loggedUser,
			dateReceived: DateUtil.getCurrentTimestamp(),

			// Assets with conditions
			assetsToReceive: [],
			allAssetTypesObj: {},
			assetConditionsMap: {},
			assetFields: [
				{ key: 'assetCode', label: 'Asset Code', sortable: true },
				{ key: 'assetType', label: 'Type', sortable: true },
				{ key: 'condition', label: 'Condition', sortable: false }
			],

			// Proof of Receipt
			proofOfReceipt: _.cloneDeep(config.proofOfReceiptDefaultValue),
			selDocuments: [null, null, null],
			files: [null, null, null],
			noOfDocuments: 1,

			// Check for loader
			isLoading: false,
		};
	},
	computed: {
		title() {
			return 'Receive Dispatch';
		},
		disableConfirmButtons() {
			return this.isLoading;
		},
		dateReceivedDisplay: {
			get() {
				return moment(this.dateReceived.startDate).format(
					'MMMM D YYYY, h:mm a'
				);
			},
			set(value) {
				this.dateReceived = value.startDate;
			},
		},
		timeStampedReceivedDate() {
			return moment(this.dateReceived.startDate).valueOf();
		},
	},
	mounted() {
		EventBus.$on('onReceiveDispatch', async (param) => {
			if (this.$refs['receive-dispatch']) {
				this.$refs['receive-dispatch'].show();
			}

			this.selDispatch = param.item;
			this.recipient = this.selDispatch.destination.company;
			this.allAssetTypesObj = param.allAssetTypesObj || {};

			// Initialize assets to receive with conditions
			await this.initializeAssetsToReceive();
		});
	},
	methods: {
		async handleOk(evt) {
			// Prevent modal from closing
			evt.preventDefault();

			// Validate form
			const isValid = await this.$validator.validateAll();
			if (!isValid) {
				this.$toaster.warning('Please fix validation errors before proceeding.');
				return;
			}

			// Validate asset conditions
			if (!this.validateAssetConditions()) {
				return;
			}

			// Validate documents format (PNG/JPG only)
			for (let i = 0; i < this.selDocuments.length; i++) {
				if (this.selDocuments[i] !== null) {
					const doc = this.selDocuments[i];
					if (!doc.file.type.match(/image\/(png|jpeg|jpg)/)) {
						this.$toaster.warning(`Document ${i + 1} must be PNG or JPG format.`);
						return;
					}
				}
			}

			// show loading indicator
			this.isLoading = true;

			// set the actual assets to have the expected assets for now
			this.selDispatch.actualAssets = this.selDispatch.assets;

			// No 1 up 1 down by default
			this.selDispatch.apply1Up1Down = false;

			// Update the scannedAssets field in destinationScanners
			this.selDispatch.destinationScanners = UserUtil.getDefaultScanners(this.allUsersObj, this.loggedUserCompany.id);
			let destinationScanners = this.selDispatch.destinationScanners
			let scanner = _.find(destinationScanners, (o) => {
				return o.id === this.loggedUser.id;
			});
			scanner.scannedAssets = this.selDispatch.actualAssets;

			//Populate auto field with received assets
			this.selDispatch.inputAssetLog.receipt.auto = this.selDispatch.actualAssets;

			//Update remarks
			this.selDispatch.notes = this.selDispatch.notes + "; " +
				`Dispatch ${this.selDispatch.dispatchId} was received by ${this.selDispatch.source.company} User via CMS.
                All assets are auto-received, no scanning was conducted`;

			// Upload proof of receipt images if any
			let hasDocuments = this.selDocuments.some(doc => doc !== null);
			if (hasDocuments) {
				try {
					await this.uploadDocuments();
				} catch (uploadError) {
					this.$toaster.error('Error uploading proof of receipt images. Please try again.');
					this.isLoading = false;
					return;
				}
			}

			// Set proof of receipt metadata
			if (hasDocuments || this.proofOfReceipt.remarks) {
				this.proofOfReceipt.recipientName = `${this.loggedUser.firstName} ${this.loggedUser.lastName}`;
				this.proofOfReceipt.approvedBy = `${this.loggedUser.firstName} ${this.loggedUser.lastName}`;
				this.proofOfReceipt.dateApproved = this.timeStampedReceivedDate;
				this.selDispatch.proofOfReceipt = _.cloneDeep(this.proofOfReceipt);
			}

			// Update dispatch with new asset conditions
			this.selDispatch.assetConditions = { ...this.assetConditionsMap };

			let dispatchId = this.selDispatch.dispatchId;
			try {
				let { data } = await dispatchApi.receiveDispatch(
					this.selDispatch,
					this.loggedUser.id,
					this.timeStampedReceivedDate
				);

				if (data.isSuccess) {
					this.$toaster.success(`Dispatch "${this.selDispatch.dispatchId}" was received successfully.`);
					this.$refs['receive-dispatch'].hide();
					EventBus.$emit('onCloseReceiveDispatch', data.dispatch);
				} else {
					this.$toaster.error(data.message);
				}
			} catch (_error) {
				this.$toaster.error(`Error receiving dispatch ${dispatchId}. Please try again.`);
			}

			// hide loading indicator
			this.isLoading = false;
		},
		onReset() {
			this.dateReceived = DateUtil.getCurrentTimestamp();
			this.proofOfReceipt = _.cloneDeep(config.proofOfReceiptDefaultValue);
			this.selDocuments = [null, null, null];
			this.files = [null, null, null];
			this.noOfDocuments = 1;
		},
		// Document handling methods
		onAddDocument() {
			if (this.noOfDocuments < 3) {
				this.noOfDocuments++;
			}
		},
		onSelectDocument(event, index) {
			const file = event.target.files[0];
			if (!file) return;

			// Validate file type
			if (!file.type.match(/image\/(png|jpeg|jpg)/)) {
				this.$toaster.warning('Only PNG and JPG images are allowed.');
				this.files[index] = null;
				return;
			}

			// Validate file size (max 1MB)
			if (file.size > 1024 * 1024) {
				this.$toaster.warning('Image size must not exceed 1MB.');
				this.files[index] = null;
				return;
			}

			// Check image dimensions
			const reader = new FileReader();
			reader.onload = (e) => {
				const img = new Image();
				img.onload = () => {
					if (img.width > 1000 || img.height > 1000) {
						this.$toaster.warning(`Image dimensions exceed 1000x1000px (${img.width}x${img.height}). It will be uploaded but may affect performance.`);
					}
				};
				img.src = e.target.result;
			};
			reader.readAsDataURL(file);

			// Set document object
			this.selDocuments[index] = {
				name: `POR_${this.selDispatch.dispatchId}_${index + 1}.jpg`,
				file: file,
				url: '',
				fbStoragePath: 'images/proofOfReceipt',
				isNew: true
			};
		},
		onResetDocument(index) {
			this.selDocuments[index] = null;
			this.files[index] = null;
			if (this.$refs[`document-${index + 1}`]) {
				this.$refs[`document-${index + 1}`].reset();
			}
		},
		onShowDocument(document) {
			if (document && document.file) {
				const reader = new FileReader();
				reader.onload = (e) => {
					window.open(e.target.result, '_blank');
				};
				reader.readAsDataURL(document.file);
			}
		},
		isNonNullDocument(index) {
			return this.selDocuments[index] !== null;
		},
		async uploadDocuments() {
			const uploadPromises = [];

			for (let i = 0; i < this.selDocuments.length; i++) {
				if (this.selDocuments[i] !== null && this.selDocuments[i].isNew) {
					uploadPromises.push(this.firebaseUpload(this.selDocuments[i]));
				}
			}

			const uploadedDocs = await Promise.all(uploadPromises);

			// Filter out null values and add to proof of receipt images
			this.proofOfReceipt.images = uploadedDocs.filter(doc => doc !== null);
		},
		async firebaseUpload(document) {
			try {
				const storageRef = storage.ref();
				const fileRef = storageRef.child(`${document.fbStoragePath}/${document.name}`);

				await fileRef.put(document.file);
				const downloadURL = await fileRef.getDownloadURL();

				return {
					name: document.name,
					url: downloadURL,
					fbStoragePath: document.fbStoragePath
				};
			} catch (error) {
				console.error('Error uploading document:', error);
				throw error;
			}
		},
		// Asset condition methods
		async initializeAssetsToReceive() {
			// Use actualAssets if it has items, otherwise fall back to assets
			const assetCodes = (this.selDispatch.actualAssets && this.selDispatch.actualAssets.length > 0)
				? this.selDispatch.actualAssets
				: (this.selDispatch.assets || []);

			if (assetCodes.length === 0) {
				this.assetsToReceive = [];
				this.assetConditionsMap = {};
				return;
			}

			// Fetch asset details to get asset types
			const assetsObjResult = await assetDAO.getAssetsByCode(assetCodes);
			const assetsObj = assetsObjResult[0];

			// Build assets list with conditions
			this.assetsToReceive = assetCodes.map(code => {
				const asset = assetsObj[code];
				const assetConditions = this.selDispatch.assetConditions;

				return {
					assetCode: code,
					assetType: (asset && asset.assetType) ? asset.assetType : '',
					assetTypeId: (asset && asset.assetTypeId) ? asset.assetTypeId : '',
					condition: (assetConditions && assetConditions[code]) ? assetConditions[code] :
						(asset && asset.details && asset.details.condition) ? asset.details.condition :
						null
				};
			});

			// Initialize condition map
			this.assetConditionsMap = {};
			this.assetsToReceive.forEach(asset => {
				this.assetConditionsMap[asset.assetCode] = asset.condition;
			});
		},
		getConditionOptions() {
			// Use global config for simplicity
			return config.assetConditionStatus;
		},
		getConditionClass(condition) {
			const classMap = {
				'Good': 'border-success',
				'Damaged': 'border-danger',
				'Outright': 'border-info',
				'Lease': 'border-warning'
			};
			return classMap[condition] || '';
		},
		validateAssetConditions() {
			const missing = [];
			this.assetsToReceive.forEach(asset => {
				if (!this.assetConditionsMap[asset.assetCode]) {
					missing.push(asset.assetCode);
				}
			});

			if (missing.length > 0) {
				this.$toaster.warning('Please select condition for all assets before receiving.');
				return false;
			}
			return true;
		}
	},
	beforeDestroy() {
		EventBus.$off('onReceiveDispatch');
	},
};
</script>

<style scoped>
.document-file {
	margin-top: 10px;
	padding: 8px;
	background-color: #f0f0f0;
	border-radius: 4px;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.document-file i {
	cursor: pointer;
	color: #20A8D8;
	flex: 1;
}

.document-file i:hover {
	text-decoration: underline;
}

.document-file em {
	cursor: pointer;
	color: #f86c6b;
	font-size: 18px;
}

.document-file em:hover {
	color: #e55353;
}

.add-document {
	margin-top: 15px;
}

.numFont {
	font-family: 'Courier New', Courier, monospace;
}

.border-success {
	border-left: 3px solid #4dbd74 !important;
}

.border-danger {
	border-left: 3px solid #f86c6b !important;
}

.border-info {
	border-left: 3px solid #63c2de !important;
}

.border-warning {
	border-left: 3px solid #ffc107 !important;
}
</style>
